import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Switch,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestoreIcon from '@mui/icons-material/Restore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { locationEquivalenciesApi, locationAssignmentsApi } from '../../api/services';
import type { LocationProductEquivalency, ProductEquivalencyMembership } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';

interface LocationEquivalenciesPanelProps {
  locationId: number;
  locationName: string;
}

export const LocationEquivalenciesPanel: React.FC<LocationEquivalenciesPanelProps> = ({
  locationId,
  locationName,
}) => {
  const queryClient = useQueryClient();
  const [editingValues, setEditingValues] = React.useState<Record<number, string>>({});
  const [editingPositions, setEditingPositions] = React.useState<Record<number, string>>({});
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['location_equivalencies', locationId],
    queryFn: () => locationEquivalenciesApi.byLocation(locationId),
    enabled: !!locationId,
  });

  const { data: allAssignments = [] } = useQuery({
    queryKey: ['location_assignments_all', locationId],
    queryFn: () => locationAssignmentsApi.getAll({ location_id: locationId }),
    enabled: !!locationId,
  });
  const activeSkus = new Set(allAssignments.filter(a => a.status === 'active').map(a => a.sku));
  const inactiveSkus = new Set(allAssignments.filter(a => a.status !== 'active').map(a => a.sku));

  // Hide equivalencies that have no product members
  const filteredSettings = settings.filter(
    (s) => (s.product_equivalency?.product_equivalency_memberships?.length ?? 0) > 0
  );

  // Reflect the report/dashboard order in the panel (sc-6000): group by
  // category (alphabetical, null/Uncategorized last), then within a category by
  // position (NULLS LAST — unpositioned groups sort after positioned ones),
  // then by equivalency name. Mirrors the shared inventory_rollup ORDER BY so
  // this editor previews the order it configures. Two intentional
  // approximations of that SQL sort, both immaterial in practice: the name
  // tiebreak uses JS localeCompare rather than the DB collation, and a category
  // literally named "Uncategorized" would render as its own trailing section
  // instead of merging with null-category groups (no real category is named
  // that; the realistic null-category case already matches the report).
  const categoryOf = (s: LocationProductEquivalency) =>
    s.product_equivalency?.product_equivalency_category?.name ?? 'Uncategorized';
  const sortedSettings = [...filteredSettings].sort((a, b) => {
    const catA = a.product_equivalency?.product_equivalency_category?.name ?? null;
    const catB = b.product_equivalency?.product_equivalency_category?.name ?? null;
    if (catA !== catB) {
      if (catA === null) return 1; // Uncategorized last
      if (catB === null) return -1;
      return catA.localeCompare(catB);
    }
    const posA = a.position ?? null;
    const posB = b.position ?? null;
    if (posA !== posB) {
      if (posA === null) return 1; // unpositioned last (NULLS LAST)
      if (posB === null) return -1;
      return posA - posB;
    }
    return (a.product_equivalency?.name ?? '').localeCompare(b.product_equivalency?.name ?? '');
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ max_count: number | null; position: number | null; status: string; notes: string; member_visibility_overrides: Record<string, { hide_when_zero: boolean } | null> }> }) =>
      locationEquivalenciesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location_equivalencies', locationId] });
      setSnackbar({ message: 'Equivalency setting updated', severity: 'success' });
    },
    onError: (err: unknown) => {
      setSnackbar({ message: extractApiError(err, 'Failed to update equivalency setting'), severity: 'error' });
    },
  });

  const toggleExpanded = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleOverrideToggle = (setting: LocationProductEquivalency, mem: ProductEquivalencyMembership, newHideValue: boolean) => {
    updateMutation.mutate({ id: setting.id, data: { member_visibility_overrides: { [mem.sku]: { hide_when_zero: newHideValue } } } });
  };

  const handleOverrideReset = (setting: LocationProductEquivalency, sku: string) => {
    updateMutation.mutate({ id: setting.id, data: { member_visibility_overrides: { [sku]: null } } });
  };

  const handleSaveMaxCount = (setting: LocationProductEquivalency) => {
    const value = editingValues[setting.id];
    if (value === undefined) return;
    const maxCount = value === '' ? null : parseInt(value, 10);
    if (maxCount !== null && isNaN(maxCount)) return;
    updateMutation.mutate({ id: setting.id, data: { max_count: maxCount } });
    setEditingValues((prev) => {
      const next = { ...prev };
      delete next[setting.id];
      return next;
    });
  };

  const handleSavePosition = (setting: LocationProductEquivalency) => {
    const value = editingPositions[setting.id];
    if (value === undefined) return;
    const position = value === '' ? null : parseInt(value, 10);
    if (position !== null && isNaN(position)) return;
    updateMutation.mutate({ id: setting.id, data: { position } });
    setEditingPositions((prev) => {
      const next = { ...prev };
      delete next[setting.id];
      return next;
    });
  };

  if (!locationId) {
    return (
      <Typography variant="body2" color="textSecondary">
        Select a location to manage equivalency settings.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Product Equivalency Settings — {locationName}
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Equivalency</TableCell>
              <TableCell>Members</TableCell>
              <TableCell sx={{ width: 110 }}>
                <Tooltip title="Display order of this group within its category on the customer report and dashboard. Leave blank to sort alphabetically after positioned groups.">
                  <span>Position</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: 140 }}>Max (total units)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4}>Loading...</TableCell>
              </TableRow>
            ) : filteredSettings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary" align="center">
                    No product equivalency settings for this location.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedSettings.map((setting, idx) => {
                const group = setting.product_equivalency;
                const cat = categoryOf(setting);
                const showCategoryHeader = idx === 0 || categoryOf(sortedSettings[idx - 1]) !== cat;
                const memberships = group?.product_equivalency_memberships || [];
                const isEditing = editingValues[setting.id] !== undefined;
                const currentValue = isEditing
                  ? editingValues[setting.id]
                  : (setting.max_count?.toString() ?? '');
                const isEditingPosition = editingPositions[setting.id] !== undefined;
                const currentPosition = isEditingPosition
                  ? editingPositions[setting.id]
                  : (setting.position?.toString() ?? '');
                const isExpanded = expandedRows.has(setting.id);
                const overrides = setting.member_visibility_overrides || {};
                const hasOverrides = Object.keys(overrides).length > 0;

                return (
                  <React.Fragment key={setting.id}>
                    {showCategoryHeader && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ bgcolor: 'action.hover', py: 0.75 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {cat}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                      onClick={() => toggleExpanded(setting.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {group?.hexa_hq_sku || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {group?.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {memberships.map((m) => {
                            const isHexa = m.product?.manufacturer?.name === 'Hexa';
                            const isActive = activeSkus.has(m.sku);
                            const isInactive = !isActive && inactiveSkus.has(m.sku);
                            const override = overrides[m.sku];
                            const hasOverride = override !== undefined;
                            const overrideHides = hasOverride && override.hide_when_zero;

                            const tooltipStatus = isActive
                              ? 'Active at this location'
                              : isInactive
                                ? 'Assigned but inactive at this location'
                                : 'Not assigned to this location';

                            return (
                              <Tooltip
                                key={m.sku}
                                title={(isHexa ? 'Hexa product — ' : '') + tooltipStatus + (hasOverride ? ` (override: ${overrideHides ? 'hidden' : 'shown'} on digest)` : '')}
                              >
                                <Chip
                                  label={m.sku}
                                  size="small"
                                  icon={isHexa
                                    ? <StarIcon fontSize="small" />
                                    : overrideHides
                                      ? <VisibilityOffIcon fontSize="small" />
                                      : isActive
                                        ? <CheckCircleIcon fontSize="small" />
                                        : undefined}
                                  color={isInactive ? 'warning' : isHexa ? 'primary' : isActive ? 'success' : 'default'}
                                  variant={isActive ? 'filled' : 'outlined'}
                                  sx={!isActive ? { opacity: 0.5 } : overrideHides ? { borderStyle: 'dashed' } : undefined}
                                />
                              </Tooltip>
                            );
                          })}
                          {hasOverrides && (
                            <Chip label="digest overrides" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={currentPosition}
                            placeholder="—"
                            onChange={(e) =>
                              setEditingPositions((prev) => ({ ...prev, [setting.id]: e.target.value }))
                            }
                            inputProps={{ min: 0, style: { width: 50 } }}
                          />
                          {/* Fixed-width slot reserves space so the input doesn't shift when the Save icon appears on edit. */}
                          <Box sx={{ width: 30, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                            {isEditingPosition && (
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSavePosition(setting)}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={currentValue}
                            onChange={(e) =>
                              setEditingValues((prev) => ({ ...prev, [setting.id]: e.target.value }))
                            }
                            inputProps={{ min: 0, style: { width: 60 } }}
                          />
                          {/* Fixed-width slot reserves space so the input doesn't shift when the Save icon appears on edit. */}
                          <Box sx={{ width: 30, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                            {isEditing && (
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSaveMaxCount(setting)}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 1, px: 3, bgcolor: 'action.hover' }}>
                          <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                            Restock Digest Visibility
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Control which members appear on the restock digest email when their quantity is zero at this location.
                            The global default applies everywhere unless overridden here.
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ py: 0.5 }}>SKU</TableCell>
                                <TableCell sx={{ py: 0.5 }}>Global Default</TableCell>
                                <TableCell sx={{ py: 0.5 }}>This Location</TableCell>
                                <TableCell sx={{ py: 0.5, width: 60 }} />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {memberships.map((m) => {
                                const globalHide = m.hide_when_zero ?? false;
                                const override = overrides[m.sku];
                                const hasOverride = override !== undefined;
                                const effectiveHide = hasOverride ? override.hide_when_zero : globalHide;

                                return (
                                  <TableRow key={m.sku}>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Typography variant="body2">{m.sku}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Chip
                                        label={globalHide ? 'Hide from digest' : 'Show on digest'}
                                        size="small"
                                        color={globalHide ? 'default' : 'success'}
                                        variant="outlined"
                                        sx={{ opacity: hasOverride ? 0.4 : 1 }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Tooltip title={effectiveHide ? 'Hidden from restock digest at zero — toggle to show on digest' : 'Shown on restock digest at zero — toggle to hide from digest'}>
                                          <Switch
                                            size="small"
                                            checked={!effectiveHide}
                                            onChange={(e) => handleOverrideToggle(setting, m, !e.target.checked)}
                                            color={hasOverride ? 'info' : 'default'}
                                          />
                                        </Tooltip>
                                        <Typography variant="caption" color={hasOverride ? 'info.main' : 'text.secondary'}>
                                          {hasOverride ? (effectiveHide ? 'Hide from digest' : 'Show on digest') : (effectiveHide ? 'Hide (default)' : 'Show (default)')}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      {hasOverride && (
                                        <Tooltip title="Reset to global default">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleOverrideReset(setting, m.sku)}
                                          >
                                            <RestoreIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {snackbar && (
        <Snackbar open autoHideDuration={4000} onClose={() => setSnackbar(null)}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};
